import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AssetsAvailable,
  GetTrackV2FindParams,
  TableQueryParams,
} from '@mzic/mzic-interfaces';

import { BehaviorSubject, Observable } from 'rxjs';
import { RoyaltySplitsService } from '../royalty-splits.service';

interface AssetsAvailableModel {
  isLoading: boolean;
  assetsAvailable: AssetsAvailable[];
  assetsAvailableChecked: AssetsAvailable[];
  searchTerm: string;
  numberOfRulesNotEligible: number;
  countCheckedAssets: {
    countReleases: number;
    countTracks: number;
  };
  sort: string;
  pageNumber: number;
  pageSize: number;
  // Lazy loading state
  offset: number;
  endList: boolean;
  lazyBlockLoading: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AssetsAvailableState {
  private royaltySplitsService = inject(RoyaltySplitsService);
  private apiCallCounter = 0;

  private initialState = {
    isLoading: false,
    assetsAvailable: [],
    assetsAvailableChecked: [],
    searchTerm: '',
    numberOfRulesNotEligible: 0,
    countCheckedAssets: {
      countReleases: 0,
      countTracks: 0,
    },
    sort: '',
    pageNumber: 1,
    pageSize: 10,
    // Lazy loading state
    offset: 0,
    endList: false,
    lazyBlockLoading: false,
  };

  private state = signal<AssetsAvailableModel>(this.initialState);

  isLoadingSnapshot = computed(() => this.state().isLoading);
  assetsAvailableSnapshot = computed(() => this.state().assetsAvailable);
  searchTermSnapshot = computed(() => this.state().searchTerm);
  countCheckedAssetsSnapshot = computed(() => this.state().countCheckedAssets);
  sortSnapshot = computed(() => this.state().sort);
  pageNumberSnapshot = computed(() => this.state().pageNumber);
  pageSizeSnapshot = computed(() => this.state().pageSize);
  // Lazy loading snapshots
  offsetSnapshot = computed(() => this.state().offset);
  endListSnapshot = computed(() => this.state().endList);
  lazyBlockLoadingSnapshot = computed(() => this.state().lazyBlockLoading);

  private isLoading$ = new BehaviorSubject<boolean>(false);
  private assetsAvailable$ = new BehaviorSubject<AssetsAvailable[]>([]);
  private assetsAvailableChecked$ = new BehaviorSubject<AssetsAvailable[]>([]);
  private countCheckedAssets$ = new BehaviorSubject<{
    countReleases: number;
    countTracks: number;
  }>({
    countReleases: 0,
    countTracks: 0,
  });
  private numberOfRulesNotEligible$ = new BehaviorSubject<number>(0);
  private sort$ = new BehaviorSubject<string>('');
  // Lazy loading subjects
  private lazyBlockLoading$ = new BehaviorSubject<boolean>(false);

  get isLoading(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  get assetsAvailable(): Observable<AssetsAvailable[]> {
    return this.assetsAvailable$.asObservable();
  }

  get assetsAvailableChecked(): Observable<AssetsAvailable[]> {
    return this.assetsAvailableChecked$.asObservable();
  }

  get numberOfRulesNotEligible(): Observable<number> {
    return this.numberOfRulesNotEligible$.asObservable();
  }

  get lazyBlockLoading(): Observable<boolean> {
    return this.lazyBlockLoading$.asObservable();
  }

  constructor() {
    effect(() => {
      console.log(this.state());
    });

    this.isLoading$.pipe(takeUntilDestroyed()).subscribe((isLoading) => {
      this.state.update((state) => ({
        ...state,
        isLoading,
      }));
    });

    this.assetsAvailable$
      .pipe(takeUntilDestroyed())
      .subscribe((assetsAvailable) => {
        this.state.update((state) => ({
          ...state,
          assetsAvailable,
        }));

        this.updateCheckedAssets();
      });

    this.assetsAvailableChecked$
      .pipe(takeUntilDestroyed())
      .subscribe((assetsAvailableChecked) => {
        this.state.update((state) => ({
          ...state,
          assetsAvailableChecked,
        }));
      });

    this.countCheckedAssets$
      .pipe(takeUntilDestroyed())
      .subscribe((countCheckedAssets) => {
        this.state.update((state) => ({
          ...state,
          countCheckedAssets,
        }));
      });

    this.numberOfRulesNotEligible$
      .pipe(takeUntilDestroyed())
      .subscribe((numberOfRulesNotEligible) => {
        this.state.update((state) => ({
          ...state,
          numberOfRulesNotEligible,
        }));
      });

    this.sort$.pipe(takeUntilDestroyed()).subscribe((sort) => {
      this.state.update((state) => ({
        ...state,
        sort,
      }));
    });
  }

  loadAssets(params?: GetTrackV2FindParams) {
    console.log(`[Assets Load] Regular loadAssets called with params:`, params);
    this.isLoading$.next(true);

    if (params?.sort && params.sort !== '') {
      this.sort$.next(params.sort);
    }

    this.apiCallCounter++;
    console.log(
      `[Assets API Call #${this.apiCallCounter}] loadAssets calling API with params:`,
      params,
    );

    this.royaltySplitsService.getAssetsAvailable(params).subscribe({
      next: (assetsAvailable) => {
        console.log(
          `[Assets API Call #${this.apiCallCounter}] loadAssets response received`,
        );
        this.assetsAvailable$.next(assetsAvailable.data.content);
        this.isLoading$.next(false);
      },
      error: () => {
        this.isLoading$.next(false);
      },
    });
  }

  // Lazy loading method - appends results instead of replacing
  loadAssetsLazy(searchTerm?: string, forceInitialLoad = false) {
    console.log(
      `[Assets Lazy Load] Called with forceInitialLoad=${forceInitialLoad}, current offset=${this.state().offset}`,
    );

    // Prevent concurrent loads
    if (this.lazyBlockLoading$.value) {
      console.log('[Assets Lazy Load] Skipping - load already in progress');
      return;
    }

    const currentOffset = this.state().offset;

    // Calculate which page to load (1-based pagination)
    // offset tracks how many pages we've loaded, so next page is offset + 1
    const pageToLoad = currentOffset + 1;

    // For initial load (offset=0), show main shimmer loading
    // For subsequent loads (offset>0), show lazy block loading
    if (currentOffset === 0) {
      this.isLoading$.next(true);
    } else {
      this.lazyBlockLoading$.next(true);
    }

    // Update search term in state if provided, otherwise use existing one
    if (searchTerm !== undefined) {
      this.state.update((state) => ({ ...state, searchTerm }));
    }

    const params: GetTrackV2FindParams = {
      page: pageToLoad,
      size: this.state().pageSize,
    };

    // Add search term from state
    const currentSearchTerm = this.state().searchTerm;
    if (currentSearchTerm) {
      params.search = currentSearchTerm;
    }

    // Debug log to track pagination calls
    console.log(
      `[Assets Lazy Load] Calling API with page=${params.page}, size=${params.size}, search="${currentSearchTerm || 'none'}", offset=${currentOffset}`,
    );

    // Add sort if exists
    if (this.state().sort) {
      params.sort = this.state().sort;
    }

    this.apiCallCounter++;
    console.log(
      `[Assets API Call #${this.apiCallCounter}] loadAssetsLazy calling API with params:`,
      params,
    );

    this.royaltySplitsService.getAssetsAvailable(params).subscribe({
      next: (response) => {
        console.log(
          `[Assets API Call #${this.apiCallCounter}] loadAssetsLazy response received`,
        );
        const newAssets = response.data.content;

        // Check if we've reached the end
        if (newAssets.length < this.state().pageSize) {
          this.state.update((state) => ({ ...state, endList: true }));
        }

        // Append new assets to existing ones
        const currentAssets = this.assetsAvailable$.value;
        this.assetsAvailable$.next([...currentAssets, ...newAssets]);

        // Update offset to track how many pages have been loaded
        const newOffset = this.state().offset + 1;
        this.state.update((state) => ({ ...state, offset: newOffset }));

        console.log(
          `[Assets Lazy Load] Load completed. Loaded page: ${params.page}, New offset: ${newOffset}, Assets loaded: ${newAssets.length}, Total assets: ${[...currentAssets, ...newAssets].length}`,
        );

        // Turn off the correct loading state based on load type
        if (currentOffset === 0) {
          this.isLoading$.next(false); // Initial load - turn off main shimmer
        } else {
          this.lazyBlockLoading$.next(false); // Lazy load - turn off lazy spinner
        }
      },
      error: () => {
        // Turn off the correct loading state based on load type
        if (currentOffset === 0) {
          this.isLoading$.next(false); // Initial load error
        } else {
          this.lazyBlockLoading$.next(false); // Lazy load error
        }
      },
    });
  }

  // Search method - replaces list and resets pagination
  searchAssets(searchTerm: string) {
    this.isLoading$.next(true);

    // Reset pagination state
    this.resetPaginationState();

    const params: GetTrackV2FindParams = {
      page: 1, // Use 1-based pagination
      size: this.state().pageSize,
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    if (this.state().sort) {
      params.sort = this.state().sort;
    }

    this.apiCallCounter++;
    console.log(
      `[Assets API Call #${this.apiCallCounter}] searchAssets calling API with params:`,
      params,
    );

    this.royaltySplitsService.getAssetsAvailable(params).subscribe({
      next: (response) => {
        console.log(
          `[Assets API Call #${this.apiCallCounter}] searchAssets response received`,
        );
        const assets = response.data.content;

        // Replace entire list for search results
        this.assetsAvailable$.next(assets);

        // Check if we've reached the end
        if (assets.length < this.state().pageSize) {
          this.state.update((state) => ({ ...state, endList: true }));
        }

        // Set offset for next lazy load (1 since we just loaded 1 page)
        this.state.update((state) => ({ ...state, offset: 1 }));

        this.isLoading$.next(false);
      },
      error: () => {
        this.isLoading$.next(false);
      },
    });
  }

  // Reset pagination state
  resetPaginationState() {
    this.state.update((state) => ({
      ...state,
      offset: 0,
      endList: false,
      lazyBlockLoading: false,
      searchTerm: '', // Reset search term as well
    }));

    // Clear existing assets
    this.assetsAvailable$.next([]);
  }

  onTableQueryParamsChanged(params: TableQueryParams) {
    let hasChange = false;

    // Update pagination
    if (
      typeof params.pageNumber === 'number' &&
      params.pageNumber !== this.state().pageNumber
    ) {
      this.state.update((state) => ({
        ...state,
        pageNumber: params.pageNumber as number,
      }));
      hasChange = true;
    }

    if (
      typeof params.pageSize === 'number' &&
      params.pageSize !== this.state().pageSize
    ) {
      this.state.update((state) => ({
        ...state,
        pageSize: params.pageSize as number,
      }));
      hasChange = true;
    }

    // Update sorting
    if (params.sort && params.sort.id && params.sort.type) {
      const sortParam =
        params.sort.type !== 'default'
          ? `${params.sort.id},${params.sort.type}`
          : '';
      if (sortParam !== this.state().sort) {
        this.state.update((state) => ({ ...state, sort: sortParam }));
        hasChange = true;
      }
    }

    if (hasChange) {
      this.loadAssetsWithCurrentParams();
    }
  }

  onSearchChanged(searchTerm: string) {
    this.state.update((state) => ({
      ...state,
      searchTerm,
      pageNumber: 1, // Reset to first page when searching
    }));

    this.loadAssetsWithCurrentParams();
  }

  private loadAssetsWithCurrentParams() {
    const currentState = this.state();
    const params: GetTrackV2FindParams = {
      page: currentState.pageNumber,
      size: currentState.pageSize,
    };

    if (currentState.sort && currentState.sort !== '') {
      params.sort = currentState.sort;
    }

    if (currentState.searchTerm && currentState.searchTerm !== '') {
      params.search = currentState.searchTerm;
    }

    this.loadAssets(params);
  }

  toggleAssetChecked(id: number, edit?: boolean) {
    const updatedAssets = this.state().assetsAvailable.map((asset) => {
      if (asset.type === 'release' && asset.release.id === id) {
        return {
          ...asset,
          release: {
            ...asset.release,
            checked: !asset.release.checked,
            tracks: asset.release.tracks.map((track) => {
              return {
                ...track,
                checked: edit ? true : !asset.release.checked,
              };
            }),
          },
        };
      }

      if (asset.type === 'track' && asset.track.id === id) {
        return {
          ...asset,
          track: {
            ...asset.track,
            checked: edit ? true : !asset.track.checked,
          },
        };
      }

      return asset;
    });

    this.assetsAvailable$.next(updatedAssets);
  }

  toggleAllAssetsChecked(checked: boolean) {
    const updatedAssets = this.state().assetsAvailable.map((asset) => {
      if (asset.type === 'release') {
        const { release } = asset;
        // Only select/deselect eligible releases
        const shouldUpdateRelease = release.eligibleForSplit;

        return {
          ...asset,
          release: {
            ...release,
            checked: shouldUpdateRelease ? checked : release.checked,
            tracks: release.tracks.map((track) => ({
              ...track,
              // Only select/deselect eligible tracks within the release
              checked: track.eligibleForSplit.eligible
                ? checked
                : track.checked,
            })),
          },
        };
      }

      if (asset.type === 'track') {
        // Only select/deselect eligible tracks
        const shouldUpdateTrack = asset.track.eligibleForSplit.eligible;

        return {
          ...asset,
          track: {
            ...asset.track,
            checked: shouldUpdateTrack ? checked : asset.track.checked,
          },
        };
      }

      return asset;
    });

    this.assetsAvailable$.next(updatedAssets);
  }

  toggleAssetReleaseTrackChecked(releaseId: number, trackId: number) {
    const updatedAssets = this.state().assetsAvailable.map((asset) => {
      if (asset.type !== 'release' || asset.release.id !== releaseId) {
        return asset;
      }

      const updatedTracks = asset.release.tracks.map((track) => {
        if (track.id !== trackId) {
          return track;
        }

        return {
          ...track,
          checked: track.checked === null ? true : !track.checked,
        };
      });

      const allTracksChecked = updatedTracks.every((track) => track.checked);

      return {
        ...asset,
        release: {
          ...asset.release,
          checked: allTracksChecked,
          tracks: updatedTracks,
        },
      };
    });

    this.assetsAvailable$.next(updatedAssets);
  }

  private updateCheckedAssets() {
    const checkedAssets = this.state().assetsAvailable.filter(
      (asset) =>
        asset.release?.checked === true || asset.track?.checked === true,
    );

    const checkedReleaseIds = checkedAssets
      .filter((asset) => asset.release)
      .map((asset) => asset.release.id);

    const checkedTracksReleases = this.state()
      .assetsAvailable.filter((asset) => asset.type === 'release')
      .map((release) => {
        if (checkedReleaseIds.includes(release.release.id)) {
          return [];
        }

        const checkedTracks = release.release.tracks.filter(
          (track) => track.checked,
        );

        return checkedTracks.length > 0 ? checkedTracks : [];
      })
      .flat();

    const formattedCheckedTracksReleases = checkedTracksReleases.map(
      (track) => {
        return {
          track: track,
          type: 'track',
        } as AssetsAvailable;
      },
    );

    this.assetsAvailableChecked$.next([
      ...checkedAssets,
      ...formattedCheckedTracksReleases,
    ]);

    this.sumRulesNotEligibleForSplit();
    this.countCheckedAssets();
  }

  private sumRulesNotEligibleForSplit() {
    let numberOfRulesNotEligible = 0;

    this.state().assetsAvailable.map((assetsAvailable) => {
      if (
        assetsAvailable.type === 'track' &&
        assetsAvailable.track?.checked &&
        assetsAvailable.track?.eligibleForSplit?.ruleNotEligible
      ) {
        numberOfRulesNotEligible += 1;
      }

      if (
        assetsAvailable.type === 'release' &&
        assetsAvailable.release?.eligibleForSplit
      ) {
        assetsAvailable.release.tracks.forEach((track) => {
          if (track?.eligibleForSplit?.ruleNotEligible && track.checked) {
            numberOfRulesNotEligible += 1;
          }
        });
      }
    });

    this.numberOfRulesNotEligible$.next(numberOfRulesNotEligible);
  }

  private countCheckedAssets() {
    const countReleases = this.state().assetsAvailableChecked.filter(
      (asset) => asset.type === 'release',
    ).length;

    const countTracks = this.state().assetsAvailableChecked.filter(
      (asset) => asset.type === 'track',
    ).length;

    this.countCheckedAssets$.next({
      countReleases,
      countTracks,
    });
  }

  toggleReleaseTrackListEvent(releaseId: number) {
    const updatedAssets = this.state().assetsAvailable.map((asset) => {
      if (asset.type !== 'release' || asset.release.id !== releaseId) {
        return asset;
      }

      return {
        ...asset,
        release: {
          ...asset.release,
          childrenOpened: !asset.release.childrenOpened,
        },
      };
    });

    this.assetsAvailable$.next(updatedAssets);
  }
}
